import { getAuthHeader, handleUnauthorized, downloadBlob } from './core'

export interface BulkRenameFileResult {
  original: string
  renamed: string
  error: string | null
}

export async function bulkRenamePreview(files: File[], renameMode = 'standard'): Promise<BulkRenameFileResult[]> {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))
  const res = await fetch(`/api/bulk-rename/preview?rename_mode=${renameMode}`, {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export function bulkRenamePreviewWithProgress(
  files: File[],
  onProgress: (pct: number) => void,
  renameMode = 'standard',
): Promise<BulkRenameFileResult[]> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const form = new FormData()
    files.forEach((f) => form.append('files', f))

    xhr.open('POST', `/api/bulk-rename/preview?rename_mode=${renameMode}`)
    xhr.setRequestHeader('Authorization', getAuthHeader())
    xhr.timeout = 120_000

    // Uploadfase: 0–80 %
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 80))
      }
    }
    xhr.upload.onload = () => onProgress(82)

    xhr.onload = () => {
      onProgress(100)
      if (xhr.status === 401) { reject(new Error('Niet ingelogd')); return }
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)) }
        catch { reject(new Error('Ongeldig antwoord van server')) }
      } else {
        try { reject(new Error(JSON.parse(xhr.responseText).detail ?? xhr.statusText)) }
        catch { reject(new Error(xhr.statusText)) }
      }
    }
    xhr.onerror   = () => reject(new Error('Netwerkfout'))
    xhr.ontimeout = () => reject(new Error('Timeout'))
    xhr.send(form)
  })
}

export async function bulkRenameDownload(files: File[], applyReplacements: boolean, renameMode = 'standard'): Promise<void> {
  const form = new FormData()
  files.forEach((f) => form.append('files', f))
  const url = `/api/bulk-rename?apply_replacements=${applyReplacements}&rename_mode=${renameMode}`
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }

  const serverName = res.headers.get('X-Download-Filename')
  const today = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const dateStr = `${today.getFullYear()}${pad(today.getMonth() + 1)}${pad(today.getDate())}-${pad(today.getHours())}${pad(today.getMinutes())}`
  const fallback = serverName ?? `${dateStr} bulk rename set - ${files.length} bestanden.zip`

  await downloadBlob(res, fallback)
}
